import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getValidIntCost, isUndefinedNullOrEmpty, getValid$Cost, getProductPrice, getPromoPrice, replaceServiceName, getWirelessServRef, isWhiOrElfwPricing, filterFFPlans, filterFFFixedWireless, isScheduleSuppressedPlan, isCsiEligiblePlan } from '../common/common';
import { useFFReferralSession } from './useFFReferralSession';
import { getConfigs } from '../config';
import { setSelectedBundle } from '../store/action/OrderAction';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { normalizeReferrerUri, filterFixedWirelessProducts } from '../helpers/tfn';
import { isAlwaysOnBuyflowReferrer, readBuyflowReferrerOverride, rememberBuyflowReferrer } from '../util/buyflowOverride';
import { CALL_CENTER_HOURS } from '../callCenter/hours';
import { resolveCallCenterOpen } from '../callCenter/schedule';


const config = getConfigs();

export const useSessionData = () => {
  const { sessionResponse, orderInfo, TFN, invocaTfn, extendedInfo, referrer, customTFN, customWhiTFN, setActiveProductTab } = useSelector(
    (state) => ({
      sessionResponse: state.OrderState.sessionResponse,
      TFN: state.OrderState.TFN,
      invocaTfn: state.OrderState.invocaTfn,
      orderInfo: state.OrderState.orderInfo,
      extendedInfo: state.OrderState.extendedInfo,
      referrer: state.OrderState.referrer,
      customTFN: state.OrderState.customTFN,
      customWhiTFN: state.OrderState.customWhiTFN,
      setActiveProductTab: state.OrderState.setActiveProductTab
    }),
  );

  return {
    sessionResponse,
    orderInfo,
    TFN,
    invocaTfn,
    extendedInfo,
    referrer,
    customTFN,
    customWhiTFN,
    setActiveProductTab
  };
};

export const useServiceablitityData = () => {
  const { serviceabilityCheck, normalizedAddress, adslShippingCost, bundlesDetails, scheduleAppt, callLogId, canScheduleAppt,mrcPosProducts,scheduleLoading,isRtsVisible,rtsAttemptCount, fixedWireless, existingCustomerDetails } = useSelector(
    (state) => ({
      serviceabilityCheck: state.OrderState.serviceabilityCheck,
      normalizedAddress: state.OrderState.normalizedAddress,
      adslShippingCost: state.OrderState.adslShippingCost,
      bundlesDetails: state.OrderState.bundlesDetails,
      scheduleAppt: state.OrderState.scheduleAppt,
      callLogId: state.OrderState.callLogId,
      canScheduleAppt: state.OrderState.canScheduleAppt,
      mrcPosProducts:state.OrderState.mrcPosProducts,
      scheduleLoading: state.OrderState.scheduleLoading,
      isRtsVisible: state.OrderState.isRtsVisible,
      rtsAttemptCount: state?.OrderState?.rtsAttemptCount,
      fixedWireless: state?.OrderState?.serviceabilityCheck?.fixedWireless,
      existingCustomerDetails: state?.OrderState?.existingCustomerDetails
    }),
  );

  const isExistingCustomer = existingCustomerDetails?.existingCustomer?.trim().toLowerCase() === 'yes';

  const isFFFlow = useFFReferralSession();
  const rawAllPlans = serviceabilityCheck?.products || serviceabilityCheck?.plans;
  const rawAllFixedWireless = serviceabilityCheck?.fixedWirelessProducts;
  const allPlansServiceTerm = serviceabilityCheck?.serviceTerm;

  // The single-page checkout skips /products, so Product.js never runs and this list - not that
  // one - is what its speed selector and comparison tables render. It was the raw catalog, so a
  // Friends & Family session could pick any speed tier and any 5G SKU at FF pricing. Narrowed
  // with the same filters Product.js uses.
  //
  // Also narrowed the same way the products page narrows its lists: the reducer stores
  // serviceabilityCheck.plans by /prequal index (skipping speed-less products), which leaves
  // holes that spreading turns into undefined entries, so those are dropped; and the 5G
  // products InternetPlans.js suppresses for AT&T/T-Mobile (filterFixedWirelessProducts) are
  // removed here too.
  const allPlans = useMemo(() => {
    const plans = (rawAllPlans || []).filter(Boolean);
    const fwProducts = filterFixedWirelessProducts(rawAllFixedWireless, fixedWireless) || [];
    if (!isFFFlow) {
      return [...plans, ...fwProducts];
    }
    return [
      ...(plans.length ? filterFFPlans(plans, allPlansServiceTerm) : plans),
      ...(fwProducts.length ? filterFFFixedWireless(fwProducts, allPlansServiceTerm) : fwProducts),
    ];
  }, [rawAllPlans, rawAllFixedWireless, fixedWireless, allPlansServiceTerm, isFFFlow]);

  return {
    serviceabilityCheck,
    normalizedAddress,
    adslShippingCost,
    bundlesDetails,
    scheduleAppt,
    callLogId,
    canScheduleAppt,
    mrcPosProducts,
    scheduleLoading,
    isRtsVisible,
    rtsAttemptCount,
    fixedWireless,
    existingCustomerDetails,
    isExistingCustomer,
    allPlans,
  };
}

export const useCartData = () => {
  const { internetPlan, wirelessCartInfo = [], vasPlanInfo, serviceTerm, adslShippingCost, isProductsAdded, errorMessage, isVasOnlyFlow, isMobileContainer, languagePref, selectedBundleData, mobileProvider, generalTerms, selectedProduct, broadbandEmailError, getBroadbandLabel,isBroadbandLabelLoading, isBroadbandLabelError, extendedInfo, installType } = useSelector(
    (state) => ({
      isProductsAdded: state.OrderState.isProductsAdded,
      internetPlan: state.OrderState.internetPlan,
      bundlePlan: state.OrderState.bundlePlan,
      wirelessCartInfo: state.OrderState.wirelessCartInfo,
      vasPlanInfo: state.OrderState.vasPlanInfo,
      serviceTerm: state.OrderState.serviceabilityCheck.serviceTerm,
      adslShippingCost: state.OrderState.adslShippingCost,
      errorMessage: state.OrderState.errorMessage,
      isVasOnlyFlow: state.OrderState.isVasOnlyFlow,
      isMobileContainer: state.OrderState.isMobileContainer,
      languagePref: state.OrderState.languagePref,
      selectedBundleData: state.OrderState.selectedBundleData,
      mobileProvider: state.OrderState.mobileProvider,
      generalTerms : state.OrderState.generalTerms,
      selectedProduct: state.OrderState.selectedProduct,
      broadbandEmailError: state.OrderState.broadbandEmailError,
      getBroadbandLabel: state.OrderState.getBroadbandLabel,
      isBroadbandLabelLoading: state.OrderState.isBroadbandLabelLoading,
      isBroadbandLabelError: state.OrderState.isBroadbandLabelError,
      extendedInfo: state.OrderState.extendedInfo,
      installType: state.OrderState.installType,
    }),
  );

  const { serviceabilityCheck,mrcPosProducts } = useServiceablitityData();
  const { bundleData, plans, bundles: prequalBundleResp } = serviceabilityCheck;
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const isBundleVisible = config.SHOW_BUNDLE === 'true' && prequalBundleResp?.length > 0 && plans?.length > 0;

  let { price = 0, equipmentCost = 0, installationCost = 0, techDetail, servRef, setupCost } = internetPlan;
  // The internet plan's own monthly price, after every discount that applies to it and before
  // `price` starts accumulating other cart lines. `price` is not this by the time it is
  // returned: the mobile branch below adds the wireless plans into it. Consumers that want to
  // display the plan's price - rather than a running cart subtotal - read this.
  let internetPlanPrice = 0;
  // check for internet plan added
  const isInternetAdded = Object.keys(internetPlan).length > 0;
  // check for mobile plan added
  const { wirelessPlanInfo = [], wirelesServeRef = '' } = wirelessCartInfo;

  const isMobileAdded = wirelessPlanInfo.length > 0;
  // calculate cart count
  const getWirelessProdCount = wirelessCartInfo?.wirelessPlanInfo?.length ? (wirelessCartInfo.wirelessPlanInfo).reduce((a, { quantity }) => a + quantity, 0) : 0;
  let productCount = Object.keys(internetPlan).length > 0 ? 1 + getWirelessProdCount : getWirelessProdCount;

  // check vas products added
  const vasOrdered = !isUndefinedNullOrEmpty(vasPlanInfo) && Object.keys(vasPlanInfo).length;
  const vasPlan = vasOrdered ? vasPlanInfo.vasPlan : [];
  productCount += vasPlan.length;
  // check for ADSL
  const isADSL = isInternetAdded && techDetail === 'ADSL' && !isUndefinedNullOrEmpty(serviceTerm);
  // The waiver has to be decided here, not further down: modemFee is the cart's router line
  // item and the figure the GTM purchase event reports as revenue. Waiving the fee only in
  // the total left the line item and the analytics revenue still charging for it.
  const isFFFlow = useFFReferralSession();
  const modemFee = isInternetAdded && !isFFFlow ? parseFloat(equipmentCost).toFixed(2) : '0.00';
  let simCharge = isMobileAdded ? Number(serviceTerm?.filter(val => val?.servLevel === wirelessPlanInfo[0]?.servLevel)[0]?.servShipping) : 0;
  const isWhiAdded = isInternetAdded && (internetPlan?.servCode?.toLowerCase() === 'elfw' || internetPlan?.techDetail?.toLowerCase() === 'elfw' || internetPlan?.servCode?.toLowerCase() === 'whi');
  const isWhiWirelessAdded = isInternetAdded && internetPlan?.servCode === 'ELFW' && (internetPlan?.servLevel === 'ELFW_Unl300G' || internetPlan?.servRef === '522');
  // ELNKWEB-2948: address-contingent (CSI) Fiber, self-install chosen. Matches
  // affiliate-order-entry-web's ScheduleInstallation.js guard exactly (eligibility AND
  // installType === 'self', not eligibility alone) - switching back to Professional must
  // bring scheduling back.
  const isCsiEligible = isInternetAdded && isCsiEligiblePlan(internetPlan, extendedInfo);
  const isSelfInstall = isCsiEligible && installType === 'self';
  const isScheduleSuppressed = isInternetAdded && (isScheduleSuppressedPlan(internetPlan) || isSelfInstall);

  const isVasAdded = vasPlan && vasPlan.length > 0;
  // bundle
  const isBundleAdded = selectedBundleData ? Object.keys(selectedBundleData)?.length > 0 : false;

  let adslCost = adslShippingCost, vasPrice = 0, adslPromoPrice = 0, oneTimeCharge = 0, monthlyCharge = 0, total = 0, promoPriceValid = false, selectedServX4YPromo = {}, servX4YAmount = '0', servX4YEnd = '', servX4YUnit = 'month';
  const selectedServiceTerm = serviceTerm?.length && serviceTerm?.filter(service => service.servServRef === internetPlan.servRef)[0];

  const spanishLanguagePref = languagePref;

  const getMobilePrice = (mobilePlan, page) => {
    let mobilePrice = getPromoPrice(mobilePlan, serviceTerm);
    if (!mobilePrice || (isBundleAdded && page === 'cart')) {
      mobilePrice = getProductPrice(mobilePlan, serviceTerm);
    }
    return getValidIntCost(mobilePrice);
  };

  let mobileMrcCombinedVal = 0;
  const servType = isBundleAdded ? 'Service' : 'mrcPos';

  const chargeMrcPosIpbb = mrcPosProducts?.includes('ipbb');

  const mrcPosIntCharge = Number(selectedServiceTerm?.servX4Y?.find(item => chargeMrcPosIpbb ? item?.servType?.toLowerCase() === 'mrcpos' : item?.servType?.toLowerCase() === 'install')?.servX4YAmount) || 0;



  if (isVasOnlyFlow) {
    if (vasOrdered) {
      if (vasPlanInfo && vasPlanInfo.vasPlan) {
        vasPlanInfo.vasPlan.forEach((vasProd) => {
          vasPrice += getValidIntCost(vasProd.price);
          servRef = vasProd.servRef
        })
      }
    }
    installationCost = 0;
    oneTimeCharge = 0;
    monthlyCharge = vasPrice;
  } else {
    price = getValidIntCost(
      getPromoPrice(internetPlan, serviceTerm)
      || ((!isFFFlow && isWhiOrElfwPricing(internetPlan))
        ? getValid$Cost(internetPlan?.price)
        : getProductPrice(internetPlan, serviceTerm))
    );
    equipmentCost = isFFFlow ? 0 : getValidIntCost(equipmentCost);
    installationCost = getValidIntCost(installationCost);
    oneTimeCharge = installationCost;
    monthlyCharge = (equipmentCost + price + vasPrice);

    if (isUndefinedNullOrEmpty(adslShippingCost)) {
      adslCost = 19.95;
    }

    // check for promo price
    if (isInternetAdded && !isUndefinedNullOrEmpty(serviceTerm)) {
      selectedServX4YPromo = serviceTerm && selectedServiceTerm?.servX4Y.find(item => item.servType.toLowerCase() === 'promo') || {};
      const installCost = Number(
        serviceTerm && selectedServiceTerm?.servX4Y?.find(item => item.servType.toLowerCase() === 'install')?.servX4YAmount || 0,
      );
      oneTimeCharge = mrcPosIntCharge > 0 ? mrcPosIntCharge : installCost;
      installationCost = mrcPosIntCharge > 0 ? mrcPosIntCharge : installCost;
    }

    if (Object.keys(selectedServX4YPromo).length) {
      servX4YAmount = selectedServX4YPromo.servX4YAmount;
      servX4YEnd = selectedServX4YPromo.servX4YEnd;
      servX4YUnit = selectedServX4YPromo.servX4YUnit;

      if (getValidIntCost(servX4YAmount) < price && !isUndefinedNullOrEmpty(servX4YAmount)) {
        price = getValidIntCost(servX4YAmount)
        promoPriceValid = true;
        monthlyCharge = equipmentCost + getValidIntCost(servX4YAmount) + vasPrice;
      }
    }

    if (isADSL || promoPriceValid) {
      adslPromoPrice = `*After ${servX4YEnd}  ${parseInt(servX4YEnd) > 1 ? `${servX4YUnit}s : ${getValid$Cost(internetPlan.price)}` : `: ${getValid$Cost(price)}`}`
      oneTimeCharge = adslCost;
    }

    // Captured here, not at the return: this is the last point at which `price` is still the
    // internet plan alone. The next branch folds the wireless plans into it.
    internetPlanPrice = price;

    // check for wireless plans
    if (isMobileAdded) {
      price = price + wirelessPlanInfo.reduce((accum, item) => parseFloat(accum) + parseFloat(item.quantity * getMobilePrice(item, 'cart')), 0)
      mobileMrcCombinedVal = wirelessPlanInfo?.reduce((acc, current) => {
        return acc + serviceTerm?.find(val => val?.servLevel === current?.servLevel)?.servX4Y?.find(el => el?.servType?.toLowerCase() === servType?.toLowerCase())?.servX4YAmount * current?.quantity
      }, 0);

      oneTimeCharge += mobileMrcCombinedVal > 0 ? mobileMrcCombinedVal + simCharge : simCharge ;
      monthlyCharge = (equipmentCost + price);
      servRef = !isUndefinedNullOrEmpty(servRef) ? `${servRef},${wirelesServeRef}` : wirelesServeRef;
    }

    // check for vas plans
    // Ire script/ cjPixel vars
    const ireItems = [];
    if (vasOrdered) {
      if (vasPlan.length > 0) {
        vasPlan.forEach((vasProd) => {
          let vasArr = {};
          vasPrice += getValidIntCost(vasProd.price);
          servRef += `${!isUndefinedNullOrEmpty(servRef) ? ',' : ''}${vasProd.servRef}`;
          vasArr = {
            subTotal: getValidIntCost(vasProd.price),
            category: vasProd.technology,
            sku: vasProd.servRef,
            quantity: 1,
            name: vasProd.techDetail,
          }
          ireItems.push(vasArr);
        })
        monthlyCharge += vasPrice;
      }
      if (isInternetAdded) {
        ireItems.push({
          subTotal: getValidIntCost(price),
          category: techDetail,
          sku: internetPlan.servRef,
          quantity: 1,
          name: internetPlan.serviceName,
        });
      }
    }
  }
  total = monthlyCharge + oneTimeCharge;

  const isMrcAndBundleDiscountApplied = isBundleAdded && mrcPosProducts?.includes(selectedBundleData?.category);

  const getOneTimeChargesHeadingValue = () => {
    if (mrcPosIntCharge || mrcPosIntCharge === 0) {
      return getValid$Cost(mrcPosIntCharge);
    }
    if (isWhiAdded) {
      return getValid$Cost(setupCost);
    }
    if (isADSL) {
      return getValid$Cost(adslCost);
    }
    return getValid$Cost(
      isMrcAndBundleDiscountApplied
        ? (Number(oneTimeCharge) - bundleDiscount)?.toFixed(2)
        : oneTimeCharge?.toFixed(2)
    );
  };

  const getOneTimeChargesLabel = () => {
    if (mrcPosIntCharge > 0 && chargeMrcPosIpbb && internetPlan?.servCode === 'ipbb') {
      return `${replaceServiceName(internetPlan?.serviceName)}`;
    } else if (isADSL) {
      return t('cart.shippingCost');
    } else if (isWhiAdded) {
      return t('cart.processingFee');
    } else if (isSelfInstall) {
      // ELNKWEB-2948: CSI self-install charges a Processing & Handling fee, not an install fee.
      return t('cart.processingFee');
    }
    return t('cart.installationCost');
  }

  const getMobilePriceLabel = (mobilePlan) => {
    return `(${mobilePlan?.quantity}) ${t('cart.mobileLabel')}  ${mobilePlan?.serviceName?.replace('Plan', '')?.replace('plan', '')}`;
  }

  const getOneTimeChargesLabelValue = () => {
    if (mrcPosIntCharge > 0 && chargeMrcPosIpbb && internetPlan?.servCode === 'ipbb') {
      return getValid$Cost(Number(mrcPosIntCharge)?.toFixed(2));
    } else if (isADSL) {
      return getValid$Cost(adslCost);
    } else if (isWhiAdded) {
      return getValid$Cost(setupCost);
    }
    return getValid$Cost(Number(installationCost)?.toFixed(2));
  }

  const getMobilePriceValue = (mobilePlan) => {
    return Number(serviceTerm?.find(el => el?.servLevel === mobilePlan?.servLevel)?.servX4Y?.find(el => el?.servType.toLowerCase() === servType?.toLowerCase())?.servX4YAmount*mobilePlan?.quantity) || 0
  }

  const InternetBundles = useMemo(() => {
    if (bundleData) {
      return bundleData?.filter(e => e.servCode1?.toLowerCase() === 'ipbb');
    }
  }, [bundleData]);

  const MobileBundles = useMemo(() => {
    if (bundleData) {
      return bundleData?.filter(e => e.servCode1?.toLowerCase() === 'mobile')
    }
  }, [bundleData])

  const mobileProductServRef = getWirelessServRef(wirelessPlanInfo);

  const allBundlesServRef = [];
  bundleData?.forEach(e => {
    if (e.productsServRef?.includes('ipbb*')) {
      plans?.forEach((ele) => {
        const getServRefArr = e.productsServRef?.toString();
        const replaceIpbbwithSevref = getServRefArr?.replace('ipbb*', ele.servRef) || '';
        allBundlesServRef.push({ code: e.bundleCode, servCodes: replaceIpbbwithSevref });
      });
    }
    if (e.productsServRef?.includes('mobile*')) {
      allBundlesServRef.push({ code: e.bundleCode, servCodes: `mobile${e?.productsServRef?.length}` });
    }
  });

  const updateSelectedBundle = () => {
    if ((isInternetAdded || isVasAdded || isMobileAdded) && isBundleVisible) {
      let bundles = [];
      allBundlesServRef?.forEach(element => {
        const bundleServref = element?.servCodes?.split(',');
        if (
          element?.servCodes === `mobile${wirelessPlanInfo.length}` ||
          element?.servCodes === `mobile${wirelesServeRef?.split(',')?.length}`
        ) {
          const ifExist = bundleData.find(e => e.bundleCode === element.code) || null;
          if (ifExist) {
            bundles.push(ifExist);
            return;
          }
        }
        let result = bundleServref?.every((el) => servRef?.split(',').includes(el)
          && bundleServref?.filter(val => val === el).length
          <=
          servRef?.split(',').filter(val => val === el).length
        );
        if (result) {
          const ifExist = bundleData.find(e => e.bundleCode === element.code) || null;
          bundles.push(ifExist);
        }
      });
      if (bundles?.length > 0) {
        const bundleWithMaxDiscount = bundles?.reduce((prev, current) =>
          Math.abs(Number(prev?.creditAmount)) > Math.abs(Number(current?.creditAmount)) ? prev : current,
        );
        dispatch(setSelectedBundle(bundleWithMaxDiscount));
      } else {
        dispatch(setSelectedBundle(null));
      }
    }
  }

  const bundleDiscount = isBundleAdded ? Math.abs(Number(bundleData?.find(el => el?.bundleCode === selectedBundleData?.bundleCode)?.creditAmount)) : 0;

  let sortedData = [];

  if (plans) {
    // Narrow here, not only in Product.js. InternetPlans prefers this list over the catalog
    // Product.js hands it, so an address that returns no fixed-wireless products rendered the
    // entire unfiltered plan set to a Friends & Family session - at Friends & Family prices,
    // with the router fee waived, on plans the campaign does not cover.
    //
    // Copied before sorting: Array.prototype.sort mutates, and `plans` is the array held in
    // the redux store.
    const ffCatalog = isFFFlow ? filterFFPlans(plans, serviceTerm) : plans;
    sortedData = [...ffCatalog].sort((a, b) => {
      return a.downstreamSpd - b.downstreamSpd;
    });
  }

  const productsWith12MonthFixWirelessServCode = sortedData.filter(
    item => item?.servCode === 'FixWireless' &&
      parseInt(item?.contractLength) === 12 &&
      (
        item.downstreamSpd / 1000 === 500 ||       // 500 Mbps
        item.downstreamSpd / 1000 === 2000 ||     // 2 GB
        item.downstreamSpd / 1000 === 250   // 250 Mbps
      )
  );

  return {
    isProductsAdded,
    productCount,
    internetPlan,
    wirelessCartInfo,
    vasPlanInfo,
    isInternetAdded,
    isMobileAdded,
    isADSL,
    adslCost,
    installationCost: (installationCost).toFixed(2),
    modemFee,
    simCharge,
    setupCost,
    isWhiAdded,
    isScheduleSuppressed,
    installType,
    isCsiEligible,
    isSelfInstall,
    oneTimeCharge: (oneTimeCharge).toFixed(2),
    monthlyCharge: (monthlyCharge).toFixed(2),
    internetPlanPrice: (internetPlanPrice).toFixed(2),
    total: (total).toFixed(2),
    adslPromoPrice,
    servRef,
    errorMessage,
    vasPlan,
    isMobileContainer,
    serviceTerm,
    spanishLanguagePref,
    selectedBundleData,
    bundleDiscount,
    isBundleAdded,
    isVasAdded,
    InternetBundles,
    MobileBundles,
    updateSelectedBundle,
    getOneTimeChargesHeadingValue,
    getOneTimeChargesLabel,
    getOneTimeChargesLabelValue,
    mobileProvider,
    allBundlesServRef,
    isBundleVisible,
    getMobilePriceLabel,
    getMobilePriceValue,
    getMobilePrice,
    mobileMrcCombinedVal,
    mrcPosIntCharge,
    mobileProductServRef,
    chargeMrcPosIpbb,
    isMrcAndBundleDiscountApplied,
    generalTerms,
    isWhiWirelessAdded,
    selectedProduct,
    broadbandEmailError,
    sortedData,
    productsWith12MonthFixWirelessServCode,
    getBroadbandLabel,
    isBroadbandLabelLoading,
    isBroadbandLabelError
  };
}

export const useMbox = () => {
  const { isElnkMailboxReq, mboxRequired } = useSelector(
    (state) => ({
      isElnkMailboxReq: state.OrderState.isElnkMailboxReq,
      mboxRequired: state.OrderState.mboxRequired,
    }),
  );

  return {
    isElnkMailboxReq,
    mboxRequired
  };
}

export const useVendor = () => {
  const { internetPlan, extendedInfo } = useSelector(
    (state) => ({
      extendedInfo: state.OrderState.extendedInfo,
      internetPlan: state.OrderState.internetPlan,
    }),
  );

  const vendor = extendedInfo && extendedInfo ? extendedInfo.find((result) => result.productId === internetPlan?.servRef)?.vendor : null

  if (vendor?.toLowerCase()?.includes('verizon')) {
    return { vendor: 'Verizon' };
  }

  return {
    vendor: vendor ? vendor.toUpperCase() : ''
  };
};

export const useBusinessAddr = () => {
  const { isBusinessAddress, businessInfo } = useSelector(
    (state) => ({
      isBusinessAddress: state.OrderState.isBusinessAddress,
      businessInfo: state.OrderState.businessInfo
    }),
  );

  return {
    isBusinessAddress,
    businessInfo
  };
};

export const useAccountCheck = () => {
  const { accountCheck, checkingAccount } = useSelector(
    (state) => ({
      accountCheck: state.OrderState.accountCheck,
      checkingAccount: state.OrderState.checkingAccount,
    }),
  );

  return {
    checkAccount: accountCheck,
    checkingAccount
  };
};

export const useScheduleDate = () => {
  const { scheduleDateTime = '', rawScheduleDateTime = '' } = useSelector((state) => ({
    scheduleDateTime: state.OrderState.scheduleDateTime,
    rawScheduleDateTime: state.OrderState.rawScheduleDateTime,
  }));
  return {
    scheduleDateTime,
    rawScheduleDateTime
  };
};

export const useStepsData = () => {
  const { paymentInfo, chgQuestionInfo } = useSelector((state) => ({
    paymentInfo: state.OrderState.paymentInfo,
    chgQuestionInfo: state.OrderState.chgQuestionInfo
  }));
  return {
    paymentInfo,
    chgQuestionInfo
  };
}

export const useLoading = () => {
  const { loading, scheduleLoading, expressLoading, hasError, broadbandEmailLoading } = useSelector(
    (state) => ({
      loading: state.OrderState.loading,
      scheduleLoading: state.OrderState.scheduleLoading,
      orderSubLoading: state.OrderState.orderSubLoading,
      expressLoading: state.OrderState.expressLoading,
      hasError: state.OrderState.hasError,
      broadbandEmailLoading: state.OrderState.broadbandEmailLoading
    }),
  );

  return {
    loading,
    scheduleLoading,
    expressLoading,
    hasError,
    broadbandEmailLoading
  };
};

export const useOrderSubmissionData = () => {
  const { orderSubmitResponse } = useSelector(
    (state) => ({
      orderSubmitResponse: state.OrderState.orderSubmitResponse,
    }),
  );

  return {
    orderSubmitResponse
  };
};

export const useVasData = () => {
  const { vasError, expressVasProducts, isVasOnlyFlow } = useSelector(
    (state) => ({
      expressVasProducts: state.OrderState.expressVasProducts,
      vasError: state.OrderState.vasError,
      isVasOnlyFlow: state.OrderState.isVasOnlyFlow,
    }),
  );

  return {
    vasError,
    expressVasProducts,
    isVasOnlyFlow
  };
};

export const useBuyFlow = () => {
  const { buyflow } = useSelector(
    (state) => ({
      buyflow: state.OrderState.buyflow,
    }),
  );

  return {
    buyflow,
  };
};

export const useCallCenterFlag = () => {
  const { referrer } = useSessionData();
  const location = useLocation();
  const { referrer: referrerQuery = '' } = queryString.parse(location?.search || '');
  const referrerUri = (referrerQuery || referrer || '').toString();
  const isInviteEarthlink = normalizeReferrerUri(referrerUri) === 'invite.earthlink.net';
  // halo.earthlink.net is the Friends & Family landing page, whose whole premise is that the
  // visitor never has to call in, so it joins invite.earthlink.net in being exempt from the
  // call-center clock (ELNKWEB-2988).
  //
  // Two reads, because the referrer is only observable on the landing URL: the query string
  // covers that first render, and the persisted answer covers every render after
  // `navigate(routesConfig.products)` drops the query - which is the page the plan cards, and
  // therefore the "Select Plan" button, live on.
  //
  // Decided here rather than written into config.BUYFLOW_ENABLED at mount. That earlier shape
  // shipped and did not hold in production: BUYFLOW_ENABLED is a mutable module value with a
  // public writer (window._elnkCheckout.setElnkConfig), and by the time the plan cards
  // rendered the mount-time write had been lost - the cards still showed the phone number.
  // Evaluating per render is what the invite exemption has always done.
  const isAlwaysOnReferrerNow = isAlwaysOnBuyflowReferrer(referrerUri);
  if (isAlwaysOnReferrerNow) {
    // Written on sight rather than in an effect: this is the only render at which the referrer
    // is knowable, and the write is idempotent, so a re-render costs nothing.
    rememberBuyflowReferrer();
  }
  // readBuyflowReferrerOverride also covers the pre-hash query string (`?referrer=...#/`),
  // which the router's location never sees.
  // halo.earthlink.net only (ALWAYS_ON_BUYFLOW_REFERRERS). Not invite.
  const isAlwaysOnReferrer = isAlwaysOnReferrerNow || readBuyflowReferrerOverride() === 'on';
  const isSelfServeReferrer = isInviteEarthlink || isAlwaysOnReferrer;

  const buyflow = config.BUYFLOW_ENABLED === 'true'
    ? true
    : config.BUYFLOW_ENABLED === 'false'
      ? false
      : null;
  // ELNKWEB-3246 hours (per weekday, to the minute, with holiday overrides) replace the single
  // CALL_CENTER_TIME window, which is no longer read. The ?buyflow override still stands in for
  // the clock, and the self-serve referrers above are still exempt from it.
  const checkTime = () => {
    if (typeof buyflow === 'boolean') {
      return !buyflow;
    }

    return resolveCallCenterOpen(new Date(), CALL_CENTER_HOURS);
  }
  const isAfterHours = isSelfServeReferrer ? true : !checkTime();
  const showPromoStickers = (config.SHOW_CALL_NOW === 'true' && isAfterHours);
  const showCallNowWhen = (isOpen) => !isSelfServeReferrer && config.SHOW_CALL_NOW === 'true' && isOpen;
  const showCallNow = showCallNowWhen(!isAfterHours);
  // `showCallNow` read off the clock when called rather than at render, for decisions made after
  // an await (see useSinglePageCheckoutNow).
  const getShowCallNow = () => showCallNowWhen(checkTime());
  return { showCallNow, getShowCallNow, showPromoStickers, isAfterHours, isAlwaysOnReferrer, isInviteEarthlink };
};

// ELNKWEB-3332. The single-page checkout skips /products, which is where the call-center flow
// lives, so it is offered only while the call centre is not taking calls. Keyed on `showCallNow`
// rather than `isAfterHours`: with SHOW_CALL_NOW off nobody is asked to call, so every hour is a
// buy-flow hour - and `showCallNow` already folds in the BUYFLOW_ENABLED override and the
// self-serve referrers that are exempt from the clock.
//
// Returned as a function that reads the clock when it is called. ServiceabilityCheck routes only
// after /prequal returns; a value captured at render goes stale if the call centre opens in
// between (or while the page sat open), sending the customer to /checkout only for CheckoutRoute,
// which reads the clock on arrival, to bounce them to /products.
export const useSinglePageCheckoutNow = () => {
  const { getShowCallNow } = useCallCenterFlag();
  return () => !getShowCallNow();
};

// The same decision, read at render.
export const useSinglePageCheckout = () => useSinglePageCheckoutNow()();
